"use client";

import { useEffect, useState } from "react";
import type { Workspace } from "@/lib/api";
import { fetchWorkspaceTags } from "@/lib/workspaceAvatarTags";
import {
  DEFAULT_ACTIVE_SHOP_PINS,
  resolveActiveShopPins,
  type ActiveShopPins,
} from "@/lib/workspaceShopPins";

function workspaceTagsAreLoaded(workspace?: Workspace | null): boolean {
  return Array.isArray(workspace?.tags);
}

export function useWorkspaceShopPins(workspace?: Workspace | null) {
  const workspaceId = workspace?.id?.trim() ?? "";
  const [pins, setPins] = useState<ActiveShopPins>(DEFAULT_ACTIVE_SHOP_PINS);
  const [isLoading, setIsLoading] = useState(() => Boolean(workspaceId));

  useEffect(() => {
    let cancelled = false;

    if (!workspaceId) {
      setPins({ ...DEFAULT_ACTIVE_SHOP_PINS });
      setIsLoading(false);
      return;
    }

    if (workspaceTagsAreLoaded(workspace)) {
      setPins(resolveActiveShopPins(workspace));
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    void (async () => {
      try {
        const tags = await fetchWorkspaceTags(workspaceId);
        if (cancelled) return;
        setPins(
          resolveActiveShopPins({
            ...(workspace ?? { id: workspaceId, name: "" }),
            tags,
          }),
        );
      } catch {
        if (!cancelled) {
          setPins({ ...DEFAULT_ACTIVE_SHOP_PINS });
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [workspace, workspaceId]);

  return { pins, setPins, isLoading };
}
